import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

const TimeElapsed = ({ startDateTime }) => {
    const { t } = useTranslation();
    const [timeElapsed, setTimeElapsed] = useState(null); // Inicialmente nulo, até que um valor válido seja calculado.

    const calculateTimeElapsed = (startDateTime) => {
        try {
            const now = new Date();
            const start = new Date(startDateTime);

            if (isNaN(start.getTime())) {
                throw new Error('Invalid date');
            }

            const diff = now - start;

            const years = Math.floor(diff / (1000 * 60 * 60 * 24 * 365));
            const months = Math.floor((diff % (1000 * 60 * 60 * 24 * 365)) / (1000 * 60 * 60 * 24 * 30));
            const days = Math.floor((diff % (1000 * 60 * 60 * 24 * 30)) / (1000 * 60 * 60 * 24));
            const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((diff % (1000 * 60)) / 1000);

            return {
                years,
                months,
                days,
                hours,
                minutes,
                seconds,
                valid: true,
            };
        } catch {
            return { valid: false };
        }
    };

    useEffect(() => {
        const updateElapsedTime = () => {
            const result = calculateTimeElapsed(startDateTime);
            if (result.valid) {
                setTimeElapsed(result);
            } else {
                setTimeElapsed(null); // Continua com placeholder enquanto inválido.
            }
        };

        updateElapsedTime(); // Calcula inicialmente.

        const interval = setInterval(updateElapsedTime, 1000); // Atualiza a cada segundo.

        return () => clearInterval(interval); // Limpa o intervalo quando o componente for desmontado.
    }, [startDateTime]);

    const formatTime = (value, singular, plural) => {
        return value === 1 ? `${value} ${singular}` : `${value} ${plural}`;
    };

    if (!timeElapsed) {
        return (
            <Box
                sx={{
                    height: 'calc(4 * 1.25rem)', // Aproximando a altura de três linhas de Typography.
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center'
                }}
            />
        );
    }

    return (
        <Box textAlign="center" sx={{ mt: 4 }}>
            <Typography variant="body1">{t('time_elapsed_together_label')}</Typography>
            <Typography variant="body1">
                {
                    `${formatTime(timeElapsed.years, t('time_elapsed_year_singular'), t('time_elapsed_year_plural'))}, 
                    ${formatTime(timeElapsed.months, t('time_elapsed_month_singular'), t('time_elapsed_month_plural'))}, 
                    ${formatTime(timeElapsed.days, t('time_elapsed_day_singular'), t('time_elapsed_day_plural'))}`
                }
            </Typography>
            <Typography variant="body1">
                {
                    `${formatTime(timeElapsed.hours, t('time_elapsed_hour_singular'), t('time_elapsed_hour_plural'))}, 
                    ${formatTime(timeElapsed.minutes, t('time_elapsed_minute_singular'), t('time_elapsed_minute_plural'))}, 
                    ${formatTime(timeElapsed.seconds, t('time_elapsed_second_singular'), t('time_elapsed_second_plural'))}`
                }
            </Typography>
            <Typography textAlign='center' sx={{ mb: 4, fontSize: '1.5rem' }}>
                🥰
            </Typography>
        </Box>
    );
};

export default TimeElapsed;
